import { CANONICAL_OPTIONS } from "./constants";
import type { DateRangeOption, DateRangeOptionValue, ReportDateConfig } from "./types";

/**
 * Build the dropdown option list for a given report.
 *
 * Applies the report's `includeOptions` / `excludeOptions` filters to the
 * canonical list, preserving canonical order. Throws if the resulting list is
 * empty or if `defaultOption` (when provided) is not present in the result, so
 * misconfiguration is caught at boot rather than producing a broken dropdown.
 *
 * @returns The filtered option list. Order matches `CANONICAL_OPTIONS`.
 */
export const getCanonicalOptions = (config: ReportDateConfig = {}): DateRangeOption[] => {
  const includeSet = config.includeOptions ? new Set(config.includeOptions) : null;
  const excludeSet = new Set<DateRangeOptionValue>(config.excludeOptions ?? []);

  const options = CANONICAL_OPTIONS.filter(({ value }) => {
    if (includeSet && !includeSet.has(value)) return false;
    if (excludeSet.has(value)) return false;
    return true;
  });

  if (options.length === 0) {
    throw new Error("getCanonicalOptions produced an empty option list. Check includeOptions/excludeOptions.");
  }

  if (config.defaultOption && !options.some(({ value }) => value === config.defaultOption)) {
    throw new Error(
      `defaultOption "${config.defaultOption}" is not in the resulting option list.`,
    );
  }

  return options;
};

/**
 * Resolve the default option for a report. Returns `config.defaultOption` when
 * provided, otherwise the first option in the filtered list.
 */
export const getDefaultOption = (config: ReportDateConfig = {}): DateRangeOptionValue => {
  if (config.defaultOption) return config.defaultOption;
  return getCanonicalOptions(config)[0].value;
};
